import React, { useState } from 'react';
import { SparklesIcon, CheckCircleIcon, ShieldCheckIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function ContactSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('App Support & Feedback');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name && email && message) {
      sounds.playSound('chest');
      setSubmitted(true);
    }
  };

  const faqs = [
    {
      q: 'Is Kiki and Kids – Learn Maths completely free?',
      a: 'Yes! The app is designed to be accessible to all families. There are zero third-party ads, no subscription fees, no locked levels, and no micro-transactions.'
    },
    {
      q: 'Does the app need internet or Wi-Fi to work?',
      a: 'No. The app is completely offline-first. Once downloaded from the App Store, your child can play in cars, flights, or quiet rooms without internet access.'
    },
    {
      q: 'Can multiple children play on the same family iPad?',
      a: 'Yes. The app allows parents to set up multiple child profiles (Age bands A, B, and C). Each child keeps their own island stars, stickers, and treehouse progress.'
    },
    {
      q: 'Are Telugu and Tamil voiceovers coming to the math app?',
      a: 'Yes! Our voice actors and curriculum tutors are currently preparing bilingual audio packs to match the beloved YouTube channel experience.'
    }
  ];

  return (
    <section id="contact" className="py-16 lg:py-24 bg-white text-slate-800 border-t-2 border-slate-200 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-100 text-sky-800 font-heading font-bold text-xs uppercase tracking-wider">
            <SparklesIcon className="w-4 h-4 text-sky-600" />
            <span>We Love Hearing From Families</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 tracking-tight">
            Contact Us &amp; App Support
          </h2>
          <p className="text-slate-600 font-medium text-base sm:text-lg">
            Have feedback on the maths app, a question about our YouTube channel, or want to say hello to Miss Kiki? Send us a note below.
          </p>
        </div>

        {/* Form and Contact Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto items-start">
          
          {/* Left Form */}
          <div className="lg:col-span-7 bg-slate-50 border-2 border-slate-200 rounded-[36px] p-6 sm:p-10 shadow-sm">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                  Send a Message
                </h3>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Your Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Parent, Teacher, or Educator name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-white border border-slate-300 text-sm focus:outline-none focus:border-kiki-blue"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="We will reply to this address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-white border border-slate-300 text-sm focus:outline-none focus:border-kiki-blue"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Topic</label>
                  <select
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-white border border-slate-300 text-sm font-semibold text-slate-700 focus:outline-none focus:border-kiki-blue"
                  >
                    <option value="App Support & Feedback">Learn Maths App Support &amp; Feedback</option>
                    <option value="Curriculum & Level Suggestions">Maths Curriculum &amp; Level Suggestions</option>
                    <option value="Bug Report">Technical Bug Report</option>
                    <option value="YouTube Channels">YouTube Telugu &amp; Tamil Content</option>
                    <option value="Shop Inquiry">Shop &amp; Kids Toys Inquiry</option>
                    <option value="Other">General Hello</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Message</label>
                  <textarea
                    rows={4}
                    required
                    placeholder="Tell us what's on your mind..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-white border border-slate-300 text-sm focus:outline-none focus:border-kiki-blue"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-bouncy w-full py-4 rounded-2xl bg-kiki-blue hover:bg-blue-600 text-white font-heading font-bold text-base shadow-md"
                >
                  Send Message to Miss Kiki Team
                </button>
              </form>
            ) : (
              <div className="py-12 text-center space-y-4 animate-pop">
                <div className="text-5xl">💌 🌟</div>
                <h3 className="font-heading font-black text-2xl text-slate-900">
                  Message Sent Successfully!
                </h3>
                <p className="text-slate-600 font-medium text-sm max-w-md mx-auto">
                  Thank you, <strong>{name}</strong>! We have received your note regarding <em>"{topic}"</em>. 
                  Our team reads every family message and will get back to you shortly at <strong>{email}</strong>.
                </p>
                <button
                  onClick={() => { sounds.playSound('tap'); setSubmitted(false); }}
                  className="btn-bouncy px-6 py-2.5 rounded-2xl bg-slate-900 text-white font-heading font-bold text-xs shadow-md mt-4"
                >
                  Send Another Message
                </button>
              </div>
            )}
          </div>

          {/* Right Direct Info & Quick Contact */}
          <div className="lg:col-span-5 space-y-6">
            <div className="p-6 rounded-3xl bg-sky-50/80 border-2 border-sky-200 space-y-3">
              <span className="text-xs font-heading font-bold text-kiki-blue uppercase tracking-wider block">
                Direct Email Inquiries
              </span>
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-slate-500 font-semibold block">App Support &amp; Technical Help</span>
                  <a href="mailto:support@kikiandkids.com" className="font-heading font-bold text-base text-slate-900 hover:text-kiki-blue">
                    support@kikiandkids.com
                  </a>
                </div>
                <div className="pt-2 border-t border-sky-200">
                  <span className="text-xs text-slate-500 font-semibold block">General, Press &amp; Partnerships</span>
                  <a href="mailto:hello@kikiandkids.com" className="font-heading font-bold text-base text-slate-900 hover:text-kiki-blue">
                    hello@kikiandkids.com
                  </a>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-emerald-50/80 border-2 border-emerald-200 space-y-2">
              <div className="flex items-center gap-2 text-emerald-800 font-heading font-bold text-sm">
                <ShieldCheckIcon className="w-5 h-5 text-emerald-700" />
                <span>Our Parent Privacy Guarantee</span>
              </div>
              <p className="text-xs text-emerald-900/80 leading-relaxed font-medium">
                We never share your email with third parties, marketing lists, or advertising partners. Any contact details you provide are solely used to answer your question.
              </p>
            </div>

            {/* Quick FAQs */}
            <div className="space-y-3 pt-2">
              <h4 className="font-heading font-bold text-lg text-slate-900">
                Frequently Asked Questions
              </h4>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs">
                    <span className="font-heading font-bold text-xs text-slate-900 block mb-1">
                      {faq.q}
                    </span>
                    <p className="text-xs text-slate-600 leading-relaxed font-medium">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
